<script setup>
/**
 * WHAT IT SENDS AND GETS BACK — one screen, one mechanism, shared by an action and by a
 * question whose choices come from the owner's own system.
 *
 * ── WHAT THIS EARNS ───────────────────────────────────────────────────────────────────
 * Four things used to sit permanently on the `choose_live` card — which list, which field
 * the customer reads, which field we save, and how it is formatted — beside three boxes for
 * running a test and a row per connection detail. Eleven controls, on every card, whether
 * or not a system had been chosen. They are all here, on a screen you reach by pressing
 * "Set it up", and three of the four are now a click on a real answer.
 *
 * ── WHAT IS REUSED VERBATIM, AND THE ONE THING THAT IS NOT ────────────────────────────
 * `TaskInputMap.vue` is mounted exactly as it is: its rows come from the connection's own
 * declared details rather than from a free list, and its saved-answer picker offers only
 * keys saved earlier on EVERY path here — the same `input_before_save` rule `validate.ts`
 * enforces — so the commonest problem on this card cannot be created.
 *
 * `TaskLiveMapping.vue` is NOT mounted. Its three pickers, its sample boxes and its Try-it
 * button are the block being replaced: its own docblock says typing dotted paths from
 * memory "is where this feature failed in the partner's field report", and it still renders
 * three boxes beside the pickers. `ResponsePicker.vue` is that block, done by pointing.
 * The file stays where it is and its specs stay green — the old step card still mounts it
 * until that card is deleted.
 *
 * ── "TRY IT" IS A REAL CALL, AND IT HAS TO BE ─────────────────────────────────────────
 * It runs the owner's own system for real. Nothing here can be mapped against a dry run: a
 * dry run answers `{dryRun: true, note: …}` — no list, no fields, nothing to point at. This
 * was a corrected error in the original design and is written down again so nobody
 * rediscovers it. The page owns the request; this screen asks for it.
 */
import { computed, ref, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import ResponsePicker from './ResponsePicker.vue';
import TaskInputMap from '../TaskInputMap.vue';
import WasloInput from '../WasloInput.vue';
import WasloSelect from '../WasloSelect.vue';
import { useWasloI18n } from '../../i18n.js';
import { toIntlLocale } from '../../helpers/intl.js';
import {
  LIVE_FORMATS,
  fieldsAt,
  liveSample,
  toStepRow,
} from '../../helpers/task-flow-draft.js';
import { stepBodyFrom } from '../../helpers/task-spine-edit.js';

const props = defineProps({
  /** A spine row whose step is a `choose_live` or a `call`. */
  row: { type: Object, required: true },
  /** `[{name, description, required}]` from the chosen connection, or `null`. */
  params: { type: Array, default: null },
  /**
   * Chat keys saved before this step on every path — `[{value, label, sends?}]`. `sends` is
   * what a choice's answer carries to a system (P-2.3), for the "sends:" lines.
   */
  availableKeys: { type: Array, default: () => [] },
  /** F3.7: keys saved on only SOME ways to this step, named, so their absence is explained. */
  someKeys: { type: Array, default: () => [] },
  /** Save keys the rest of the task writes. */
  takenKeys: { type: Array, default: () => [] },
  /** `{running, result, errorMessage, fieldErrors}` for this step, owned by the page. */
  test: { type: Object, default: () => ({}) },
  /** Back to the step this wiring belongs to. */
  backLink: { type: [String, Object], default: '' },
  /**
   * The details Waslo fills in that the chosen connection sends (`contactDetails` from the
   * BFF, P-3.1). Shown as locked rows: a typed or model-made value never replaces them.
   */
  contactDetails: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:row', 'try']);

const { t, locale } = useWasloI18n();

const key = name => `WASLO.TASKS.COMPOSE.WIRING.${name}`;

const format = computed(
  () => new Intl.NumberFormat(toIntlLocale(locale.value))
);

const step = computed(() => props.row.step ?? {});
const isLive = computed(() => step.value.type === 'choose_live');

/**
 * The step as the surviving components edit it, held LOCALLY.
 *
 * `toStepRow` mints a fresh local key for every input row each time it runs, and a `v-for`
 * re-keyed on every keystroke drops the caret and any in-flight Arabic composition. So the
 * draft is seeded once per step and written back out through `stepToWire`, which is the
 * pair that already carries the "a field from a newer sidecar rides along" rule.
 */
const draft = ref(toStepRow(step.value));

watch(
  () => step.value.id,
  () => {
    draft.value = toStepRow(step.value);
  }
);

const pushDraft = () =>
  emit('update:row', { ...props.row, step: stepBodyFrom(draft.value) });

const setInputs = rows => {
  draft.value = { ...draft.value, inputs: rows };
  pushDraft();
};

/**
 * Decision 5, pre-made: a detail called `studio` is filled from the answer called `studio`.
 *
 * Only ever into a BLANK row, and only on an exact name match — a near-match guessed wrong
 * is a connection called with somebody else's answer, which nothing on screen would show.
 * It emits only when something really changed, because the page treats any change to the
 * graph as a reason to drop the server's verdict off the screens.
 */
watch(
  [() => props.params, () => props.availableKeys, () => step.value.id],
  () => {
    const names = new Set(props.availableKeys.map(row => row.value));
    let changed = false;
    const rows = draft.value.inputs.map(row => {
      if (row.mode !== 'key' || (row.from ?? '') !== '') return row;
      if (!names.has(row.name)) return row;
      changed = true;
      return { ...row, from: row.name };
    });
    const missing = (props.params ?? []).filter(
      param =>
        names.has(param.name) && !rows.some(row => row.name === param.name)
    );
    if (missing.length) changed = true;
    if (!changed) return;
    setInputs([
      ...rows,
      ...missing.map(param => ({
        key: `auto-${param.name}`,
        name: param.name,
        mode: 'key',
        from: param.name,
        value: '',
      })),
    ]);
  },
  { immediate: true, deep: true }
);

// ── running it ──────────────────────────────────────────────────────────────────────
const values = ref({});

watch(
  () => [props.params, draft.value.inputs],
  ([params, inputs]) => {
    const kept = {};
    (params ?? []).forEach(param => {
      const fixed = (inputs ?? []).find(
        row => row.name === param.name && row.mode === 'value'
      );
      kept[param.name] = values.value[param.name] ?? fixed?.value ?? '';
    });
    values.value = kept;
  },
  { immediate: true, deep: true }
);

const run = () => {
  if (props.test.running) return;
  emit('try', { ...values.value });
};

const answer = computed(() =>
  props.test.result && props.test.result.ok ? props.test.result.data : null
);

/** A change connection's test answers with what it WOULD send, never with a result (P-3.4). */
const preview = computed(() => props.test.result?.preview ?? null);

const LOCKED_LABEL = {
  'contact.phone_number': 'LOCKED_PHONE',
  'contact.name': 'LOCKED_NAME',
  'contact.email': 'LOCKED_EMAIL',
  'conversation.id': 'LOCKED_CHAT',
};
const lockedRows = computed(() =>
  props.contactDetails
    .filter(name => LOCKED_LABEL[name] !== undefined)
    .map(name => ({ name, label: t(key(LOCKED_LABEL[name])) }))
);

const headline = computed(() => {
  const { result } = props.test;
  if (!result) return '';
  if (result.preview) return '';
  if (result.ok) return t(key('OK'), { status: result.status });
  return result.status === null
    ? t(key('NO_STATUS'))
    : t(key('FAILED'), { status: result.status });
});

// ── what comes back ─────────────────────────────────────────────────────────────────
const mapping = computed(() => ({
  listPath: step.value.listPath ?? '',
  labelField: step.value.labelField ?? '',
  valueField: step.value.valueField ?? '',
  format: step.value.format ?? 'text',
}));

const setMapping = next => {
  draft.value = {
    ...draft.value,
    listPath: next.listPath,
    labelField: next.labelField,
    valueField: next.valueField,
    format: next.format,
  };
  pushDraft();
};

const setKeep = next => {
  draft.value = {
    ...draft.value,
    keep: Object.entries(next).map(([field, saveAs]) => ({
      key: `keep-${field}`,
      field,
      saveAs,
    })),
  };
  pushDraft();
};

const keep = computed(() => step.value.keep ?? {});

const formatRows = computed(() =>
  LIVE_FORMATS.map(name => ({
    id: name,
    label: t(`WASLO.TASKS.EDITOR.FLOW.LIVE.FORMAT_${name.toUpperCase()}`),
    chosen: mapping.value.format === name,
  }))
);

const sample = computed(() =>
  liveSample(answer.value, mapping.value, locale.value)
);

// ── what a detail sent from a choice carries (P-2.3) ─────────────────────────────────
const sendsLines = computed(() => {
  const byKey = new Map(
    props.availableKeys.map(row => [row.value, row.sends ?? []])
  );
  return draft.value.inputs
    .filter(row => row.mode === 'key' && (byKey.get(row.from) ?? []).length > 0)
    .map(row => ({ name: row.name, values: byKey.get(row.from).join(', ') }));
});

// ── a list named in both languages (P-10.24) ─────────────────────────────────────────
const labelFields = computed(() => step.value.labelFields ?? {});
const nameChoices = computed(() => [
  { value: '', label: t(key('NAME_FROM_SAME')) },
  ...fieldsAt(answer.value, mapping.value.listPath).map(field => ({
    value: field,
    label: field,
  })),
]);
const setLabelField = (lang, field) => {
  const next = { ...labelFields.value };
  if (field) next[lang] = field;
  else delete next[lang];
  // "The same field for both" takes the key off entirely, so a list that names nothing per
  // language saves exactly as it did before P-10.24.
  const { labelFields: _old, ...source } = draft.value.source ?? {};
  draft.value = {
    ...draft.value,
    source: Object.keys(next).length
      ? { ...source, labelFields: next }
      : source,
  };
  pushDraft();
};
</script>

<template>
  <section class="flex flex-col gap-6" data-waslo-wiring-screen>
    <div class="flex flex-col gap-1">
      <router-link
        v-if="backLink"
        :to="backLink"
        class="inline-flex items-center gap-2 text-sm text-n-slate-11 hover:text-n-slate-12"
        data-waslo-wiring-back
      >
        <span
          class="size-4 i-lucide-chevron-left rtl:rotate-180"
          aria-hidden="true"
        />
        <bdi>{{ step.name || t('WASLO.TASKS.COMPOSE.SCREEN.UNWRITTEN') }}</bdi>
      </router-link>
      <h2 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t(key('HEADING')) }}
      </h2>
    </div>

    <!-- What Waslo fills in itself. Locked: not a box, so nothing typed can replace it. -->
    <div
      v-if="lockedRows.length"
      class="flex flex-col gap-2"
      data-waslo-wiring-locked
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('LOCKED_HEADING')) }}
      </p>
      <ul class="flex flex-col gap-2 mb-0">
        <li
          v-for="locked in lockedRows"
          :key="locked.name"
          class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1"
          :data-waslo-wiring-locked-row="locked.name"
        >
          <span class="flex items-center gap-2 text-sm text-n-slate-12">
            <span
              class="shrink-0 size-4 i-lucide-lock text-n-slate-11"
              aria-hidden="true"
            />
            {{ locked.label }}
          </span>
          <span
            v-if="locked.name === 'contact.phone_number'"
            class="text-sm text-n-slate-11"
          >
            {{ t(key('LOCKED_PHONE_NOTE')) }}
          </span>
        </li>
      </ul>
    </div>

    <!-- What it sends. The connection's own declared details, never a free list. -->
    <TaskInputMap
      :model-value="draft.inputs"
      :params="params"
      :available-keys="availableKeys"
      :some-keys="someKeys"
      @update:model-value="setInputs"
    />
    <ul
      v-if="sendsLines.length"
      class="flex flex-col gap-1 mb-0"
      data-waslo-wiring-sends
    >
      <li
        v-for="line in sendsLines"
        :key="line.name"
        class="text-sm text-n-slate-11"
      >
        <bdi>{{ t(key('SENDS_VALUES'), line) }}</bdi>
      </li>
    </ul>

    <!-- Running it for real. -->
    <div class="flex flex-col gap-3">
      <div v-if="(params ?? []).length" class="flex flex-col gap-3">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t(key('VALUES')) }}
        </p>
        <p class="mb-0 text-sm text-n-slate-11">{{ t(key('VALUES_HINT')) }}</p>
        <WasloInput
          v-for="param in params"
          :key="param.name"
          :model-value="values[param.name] ?? ''"
          :label="param.name"
          :hint="param.description"
          :message="test.fieldErrors?.[param.name] || ''"
          :message-type="test.fieldErrors?.[param.name] ? 'error' : 'info'"
          :data-waslo-wiring-value="param.name"
          @update:model-value="value => (values[param.name] = value)"
        />
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <Button
          :label="test.running ? t(key('RUNNING')) : t(key('TRY'))"
          icon="i-lucide-play"
          variant="faded"
          color="slate"
          size="sm"
          :is-loading="test.running"
          :disabled="test.running"
          data-waslo-wiring-try
          @click="run"
        />
        <p class="mb-0 text-sm text-n-slate-11">{{ t(key('TRY_NOTE')) }}</p>
      </div>

      <p
        v-if="test.errorMessage"
        class="mb-0 text-sm text-n-ruby-9"
        data-waslo-wiring-unavailable
      >
        {{ test.errorMessage }}
      </p>
      <!-- A change is never run from here: this is what it WOULD send (P-3.4). -->
      <div
        v-else-if="preview"
        class="flex flex-col gap-2 p-4 rounded-xl bg-n-alpha-1"
        data-waslo-wiring-preview
      >
        <p class="mb-0 text-sm text-n-slate-12">{{ t(key('PREVIEW_NOTE')) }}</p>
        <div
          class="px-3 py-2 overflow-x-auto font-mono text-xs whitespace-pre rounded-lg bg-n-alpha-2 text-n-slate-12"
          dir="ltr"
        >
          {{ preview.method }} {{ preview.url }}
        </div>
      </div>
      <div
        v-else-if="test.result"
        class="flex flex-col gap-3 p-4 outline outline-1 -outline-offset-1 rounded-xl bg-n-solid-1"
        :class="test.result.ok ? 'outline-n-weak' : 'outline-n-amber-8'"
        :data-waslo-wiring-result="test.result.ok ? 'ok' : 'failed'"
      >
        <p
          class="flex items-center gap-2 mb-0 text-sm font-medium"
          :class="test.result.ok ? 'text-n-blue-11' : 'text-n-amber-11'"
        >
          <span
            class="shrink-0 size-4"
            :class="
              test.result.ok
                ? 'i-lucide-circle-check'
                : 'i-lucide-triangle-alert'
            "
            aria-hidden="true"
          />
          {{ headline }}
        </p>
        <!-- `dir="ltr"`: a machine's answer reads left to right whatever is inside it. -->
        <div
          v-if="!test.result.ok"
          class="px-3 py-2 overflow-x-auto font-mono text-xs whitespace-pre rounded-lg bg-n-alpha-1 text-n-slate-12"
          dir="ltr"
          data-waslo-wiring-error
        >
          {{ test.result.error }}
        </div>
      </div>
    </div>

    <!-- What comes back, picked by pointing. -->
    <ResponsePicker
      :answer="answer"
      :mode="isLive ? 'live' : 'keep'"
      :mapping="mapping"
      :keep="keep"
      :taken-keys="takenKeys"
      @update:mapping="setMapping"
      @update:keep="setKeep"
    />

    <!-- How a raw value reads to a customer. One radio, in place, never a `<select>`. -->
    <div v-if="isLive && mapping.listPath" class="flex flex-col gap-2">
      <p
        id="waslo-wiring-format-label"
        class="mb-0 text-sm font-medium text-n-slate-12"
      >
        {{ t(key('SHOWN_AS')) }}
      </p>
      <div
        role="radiogroup"
        aria-labelledby="waslo-wiring-format-label"
        class="flex flex-wrap gap-2"
      >
        <button
          v-for="option in formatRows"
          :key="option.id"
          type="button"
          role="radio"
          :aria-checked="option.chosen ? 'true' : 'false'"
          class="px-3 py-1 text-sm rounded-lg outline outline-1"
          :class="
            option.chosen
              ? 'outline-n-blue-8 bg-n-blue-2 text-n-blue-11'
              : 'outline-n-weak text-n-slate-11 hover:bg-n-alpha-2'
          "
          :data-waslo-wiring-format="option.id"
          @click="setMapping({ ...mapping, format: option.id })"
        >
          {{ option.label }}
        </button>
      </div>

      <!-- One system can name a row in both languages (P-10.24). Earned by a text list
           and a real answer to point at. -->
      <div
        v-if="mapping.format === 'text' && nameChoices.length > 1"
        class="grid gap-3 mt-2 md:grid-cols-2"
        data-waslo-wiring-names
      >
        <WasloSelect
          :model-value="labelFields.en ?? ''"
          :label="t(key('NAME_EN_FROM'))"
          :options="nameChoices"
          data-waslo-wiring-name-en
          @update:model-value="value => setLabelField('en', value)"
        />
        <WasloSelect
          :model-value="labelFields.ar ?? ''"
          :label="t(key('NAME_AR_FROM'))"
          :options="nameChoices"
          data-waslo-wiring-name-ar
          @update:model-value="value => setLabelField('ar', value)"
        />
      </div>

      <div
        v-if="sample.labels.length"
        class="flex flex-col gap-2 mt-2"
        data-waslo-wiring-sample
      >
        <ul class="flex flex-wrap gap-2 mb-0">
          <li
            v-for="(label, index) in sample.labels"
            :key="index"
            class="px-3 py-1 text-sm rounded-lg outline outline-1 outline-n-weak text-n-slate-12"
            data-waslo-wiring-sample-label
          >
            <bdi>{{ label || '—' }}</bdi>
          </li>
          <li
            v-if="sample.more"
            class="px-3 py-1 text-sm rounded-lg outline outline-1 outline-n-weak text-n-slate-11"
          >
            {{ t('WASLO.TASKS.EDITOR.FLOW.LIVE.MORE') }}
          </li>
        </ul>
        <p class="mb-0 text-sm text-n-slate-11" data-waslo-wiring-shape>
          {{
            t(key(sample.total <= 3 ? 'SHAPE_BUTTONS' : 'SHAPE_LIST'), {
              count: format.format(sample.total),
            })
          }}
        </p>
        <p
          v-if="sample.missingValue"
          class="mb-0 text-sm text-n-amber-11"
          data-waslo-wiring-missing-value
        >
          {{ t('WASLO.TASKS.EDITOR.FLOW.LIVE.MISSING_VALUE') }}
        </p>
      </div>
    </div>

    <div class="flex">
      <router-link
        v-if="backLink"
        :to="backLink"
        class="inline-flex items-center h-9 px-4 text-sm font-medium rounded-lg bg-n-brand hover:bg-n-brand/90 text-white"
        data-waslo-wiring-done
      >
        {{ t(key('DONE')) }}
      </router-link>
    </div>
  </section>
</template>
